import type { Lang } from '@fakerpg/core';

export type ThemeSetting = 'system' | 'light' | 'dark';
export type AssetPackSetting = 'glyph' | 'discord';

/**
 * Presentation preferences of this browser. They are not game data: the
 * account is the bot's document (ADR-016), which has no field for them, so
 * they stay on the device like any site's display settings.
 */
export interface Prefs {
  lang: Lang;
  theme: ThemeSetting;
  assetPack: AssetPackSetting;
  reducedMotion: boolean;
  sound: boolean;
  /** The web's own first-steps tour (the bot's `onboarding` field belongs to its slash-command tutorial). */
  tour: { step: number; done: boolean };
}

export function defaultPrefs(lang: Lang): Prefs {
  return { lang, theme: 'system', assetPack: 'glyph', reducedMotion: false, sound: false, tour: { step: 0, done: false } };
}

export interface PrefsStore {
  load(): Partial<Prefs> | null;
  save(prefs: Prefs): void;
}

export const PREFS_KEY = 'fakerpg.prefs.v1';

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** localStorage-backed preferences; any storage failure falls back to defaults. */
export function localPrefsStore(storage: Storage | undefined = safe(() => window.localStorage, undefined)): PrefsStore {
  return {
    load() {
      const raw = safe(() => storage?.getItem(PREFS_KEY) ?? null, null);
      if (raw === null) return null;
      const parsed = safe<unknown>(() => JSON.parse(raw), null);
      return parsed && typeof parsed === 'object' ? (parsed as Partial<Prefs>) : null;
    },
    save(prefs) {
      safe(() => storage?.setItem(PREFS_KEY, JSON.stringify(prefs)), undefined);
    },
  };
}

export function memoryPrefsStore(initial: Partial<Prefs> | null = null): PrefsStore {
  let current = initial;
  return {
    load: () => current,
    save: (p) => {
      current = p;
    },
  };
}
